import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Card, DatePicker, Segmented, Skeleton, Tooltip } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  FilterOutlined,
  InfoCircleOutlined,
  RiseOutlined,
  FallOutlined,
  TrophyOutlined,
  BarChartOutlined,
  MinusOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { Title, ChartCard } from "components/common";
import { BarChart } from "components/charts";
import {
  useGetDashboardV2ScoreDistributionQuery,
  useGetDashboardV2PerformanceByAttributeQuery,
} from "../../api";
import { DASHBOARD_USE_MOCK_DATA } from "utils/featureFlags";
import { mockScoreDistribution, mockPerformanceByAttribute } from "../../mockData";
import styles from "./DashboardAnalyticsRow.module.less";

const { RangePicker } = DatePicker;

const DATE_PRESETS = {
  "Last 7 Days":   [moment().subtract(6,  "days").startOf("day"), moment().endOf("day")],
  "Last 30 Days":  [moment().subtract(29, "days").startOf("day"), moment().endOf("day")],
  "Last 3 Months": [moment().subtract(3,  "months").startOf("day"), moment().endOf("day")],
  "Last 6 Months": [moment().subtract(6,  "months").startOf("day"), moment().endOf("day")],
  "All Time":      [moment("2000-01-01"), moment().endOf("day")],
};

// ── Constants ─────────────────────────────────────────────────────────────────
const PASS_THRESHOLD = 60;
const MAX_LABEL_LEN  = 16; // truncate long script names on x-axis

const SCORE_BUCKETS = [
  { range: "0–20%",   min: 0,  max: 20,  color: "#ff4d4f" },
  { range: "20–40%",  min: 20, max: 40,  color: "#ff7a45" },
  { range: "40–60%",  min: 40, max: 60,  color: "#faad14" },
  { range: "60–80%",  min: 60, max: 80,  color: "#73d13d" },
  { range: "80–100%", min: 80, max: 101, color: "#52c41a" },
];

const ATTR_OPTIONS = [
  { label: "Specialty",   value: "specialty"   },
  { label: "Script",      value: "script"      },
  { label: "Topic",       value: "topic"       },
  { label: "Explanation", value: "explanation" },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
const truncate = (str, len = MAX_LABEL_LEN) =>
  str && str.length > len ? `${str.slice(0, len)}…` : str;

const computeDistribution = (latestQuizzes = []) => {
  const buckets = SCORE_BUCKETS.map((b) => ({ ...b, count: 0 }));
  latestQuizzes.forEach((q) => {
    const acc    = parseFloat(q.accuracy ?? 0);
    const bucket = buckets.find((b) => acc >= b.min && acc < b.max);
    if (bucket) bucket.count++;
  });
  return buckets.map((b) => ({ range: b.range, count: b.count, color: b.color }));
};

// Derive summary insights from quiz list
const computeDistributionStats = (latestQuizzes = [], distributionData = []) => {
  if (!latestQuizzes.length) return null;

  const accuracies = latestQuizzes.map((q) => parseFloat(q.accuracy ?? 0));
  const avg        = accuracies.reduce((s, a) => s + a, 0) / accuracies.length;
  const best       = Math.max(...accuracies);

  // Trend: avg of last 3 vs avg of first 3
  const sample = Math.min(3, Math.floor(accuracies.length / 2));
  const recent = accuracies.slice(-sample).reduce((s, a) => s + a, 0) / sample;
  const early  = accuracies.slice(0, sample).reduce((s, a) => s + a, 0) / sample;
  const trend  = parseFloat((recent - early).toFixed(1));

  // Top range (most quizzes)
  const topBucket = [...distributionData].sort((a, b) => b.count - a.count)[0];
  const topRange  = topBucket?.count > 0 ? topBucket : null;
  const topColor  = SCORE_BUCKETS.find((b) => b.range === topRange?.range)?.color;

  return {
    avg:      parseFloat(avg.toFixed(1)),
    best:     parseFloat(best.toFixed(1)),
    trend,
    topRange,
    topColor,
  };
};

const computeSpecialtyAccuracy = (specialityPerformance = {}) =>
  Object.entries(specialityPerformance)
    .map(([specialty, obj]) => {
      const correct   = obj?.correct   ?? 0;
      const incorrect = obj?.incorrect ?? 0;
      const total     = correct + incorrect;
      const accuracy  = total > 0 ? Math.round((correct / total) * 100) : 0;
      return { label: specialty, fullLabel: specialty, accuracy, correct, incorrect, total, passing: accuracy >= PASS_THRESHOLD };
    })
    .sort((a, b) => b.accuracy - a.accuracy);

// Groups latestQuizzes by script name — avg accuracy per script
const computeScriptAccuracy = (latestQuizzes = []) => {
  const map = {};
  latestQuizzes.forEach((q) => {
    const name = q.quizScriptName || "Unknown";
    if (!map[name]) map[name] = { accuracies: [] };
    map[name].accuracies.push(parseFloat(q.accuracy ?? 0));
  });
  return Object.entries(map)
    .map(([name, { accuracies }]) => {
      const accuracy = Math.round(accuracies.reduce((s, a) => s + a, 0) / accuracies.length);
      return {
        label:      truncate(name),
        fullLabel:  name,
        accuracy,
        attempts: accuracies.length,
        passing:  accuracy >= PASS_THRESHOLD,
      };
    })
    .sort((a, b) => b.accuracy - a.accuracy);
};

// ── Skeleton ──────────────────────────────────────────────────────────────────
const AnalyticsRowSkeleton = () => (
  <div className={styles.row}>
    {[1, 2].map((i) => (
      <Card key={i} className={styles.analyticsCard}>
        <div className={styles.sectionHeader}>
          <Skeleton.Input active size="small" className={styles.skeletonTitle} />
        </div>
        <Skeleton active title={false} paragraph={{ rows: 7 }} className={styles.skeletonChartArea} />
      </Card>
    ))}
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────
const DashboardAnalyticsRow = () => {
  const user   = useSelector((state) => state?.app?.user?.info);
  const userId = user?.user_code ?? user?._id;

  const [attribute, setAttribute] = useState("specialty");
  const [dateRange, setDateRange] = useState(null);

  // ── Score Distribution query (date range → server-side filter when live) ──
  const distQueryBody = useMemo(() => ({
    userId,
    ...(dateRange?.[0] && { startDate: dateRange[0].format("YYYY-MM-DD") }),
    ...(dateRange?.[1] && { endDate:   dateRange[1].format("YYYY-MM-DD") }),
  }), [userId, dateRange]);

  const {
    data: distData,
    isLoading: distLoading,
    isFetching: distFetching,
  } = useGetDashboardV2ScoreDistributionQuery(
    distQueryBody,
    { skip: !userId || DASHBOARD_USE_MOCK_DATA, refetchOnMountOrArgChange: true }
  );

  const allQuizzes = DASHBOARD_USE_MOCK_DATA
    ? (mockScoreDistribution?.quizzes ?? [])
    : (distData?.quizzes ?? []);

  // Mock: filter client-side; Live: backend already filtered
  const filteredQuizzes = useMemo(() => {
    if (!DASHBOARD_USE_MOCK_DATA || !dateRange?.[0] || !dateRange?.[1]) return allQuizzes;
    const from = dateRange[0].valueOf();
    const to   = dateRange[1].valueOf();
    return allQuizzes.filter((q) => new Date(q.date).getTime() >= from && new Date(q.date).getTime() <= to);
  }, [allQuizzes, dateRange]);

  const distributionData = useMemo(() => computeDistribution(filteredQuizzes),                        [filteredQuizzes]);
  const distStats        = useMemo(() => computeDistributionStats(filteredQuizzes, distributionData), [filteredQuizzes, distributionData]);

  const totalQuizzes   = filteredQuizzes.length;
  const passingQuizzes = filteredQuizzes.filter((q) => parseFloat(q.accuracy ?? 0) >= PASS_THRESHOLD).length;
  const passRate       = totalQuizzes > 0 ? Math.round((passingQuizzes / totalQuizzes) * 100) : null;

  // ── Performance by Attribute query (refetches when attribute changes) ──────
  const attrQueryBody = useMemo(() => ({
    userId,
    performanceBy: attribute,
  }), [userId, attribute]);

  const {
    data:      attrData,
    isLoading: attrLoading,
    isFetching: attrFetching,
  } = useGetDashboardV2PerformanceByAttributeQuery(
    attrQueryBody,
    { skip: !userId || DASHBOARD_USE_MOCK_DATA, refetchOnMountOrArgChange: true }
  );

  // Map API/mock items → internal shape with `passing` flag
  const activeAttrData = useMemo(() => {
    const raw = DASHBOARD_USE_MOCK_DATA
      ? (mockPerformanceByAttribute[attribute] ?? [])
      : (attrData?.items ?? []);
    return raw
      .map((item) => ({ ...item, label: item.name, fullLabel: item.name, passing: item.accuracy >= PASS_THRESHOLD }))
      .sort((a, b) => b.accuracy - a.accuracy);
  }, [attribute, attrData]);

  const passingCount = activeAttrData.filter((d) => d.passing).length;
  const totalCount   = activeAttrData.length;

  if (distLoading && !allQuizzes.length) return <AnalyticsRowSkeleton />;

  const hasDistributionData = distributionData.some((b) => b.count > 0);
  const hasAttrData         = activeAttrData.length > 0;

  const activeAttrLabel = ATTR_OPTIONS.find((o) => o.value === attribute)?.label ?? attribute;

  return (
    <div className={styles.row}>

      {/* ── Score Distribution ─────────────────────────────────────────── */}
      <Card className={styles.analyticsCard}>
        <div className={styles.cardContentWrap}>
          <div className={styles.sectionHeader}>
            <div className={styles.headerLeft}>
              <Title level={5} className={styles.sectionTitle}>
                Score Distribution
              </Title>
              {passRate !== null && (
                <Tooltip
                  title="Percentage of your quizzes that scored 60% or above"
                  placement="top"
                >
                  <span className={passRate >= PASS_THRESHOLD ? styles.badgeGreen : styles.badgeRed}>
                    {passRate >= PASS_THRESHOLD
                      ? <CheckCircleOutlined />
                      : <CloseCircleOutlined />
                    }
                    {passRate}% pass rate
                  </span>
                </Tooltip>
              )}
            </div>
            <div className={styles.filterGroup}>
              <Tooltip title="Filter by date range" placement="topRight">
                <FilterOutlined className={styles.filterIcon} />
              </Tooltip>
              <RangePicker
                size="small"
                value={dateRange}
                ranges={DATE_PRESETS}
                format="MMM D, YYYY"
                allowClear
                onChange={setDateRange}
                placeholder={["From", "To"]}
              />
            </div>
          </div>
          {dateRange && (
            <div className={styles.activeFilterBanner}>
              Showing <strong>{totalQuizzes}</strong> quiz{totalQuizzes !== 1 ? "zes" : ""}
              {" "}from <strong>{dateRange[0]?.format("MMM D, YYYY")}</strong>
              {" "}to <strong>{dateRange[1]?.format("MMM D, YYYY")}</strong>
              <button className={styles.clearFilter} onClick={() => setDateRange(null)}>
                Clear
              </button>
            </div>
          )}

          <ChartCard>
            {hasDistributionData ? (
              <BarChart
                data={distributionData}
                xField="range"
                yField="count"
                color={({ range }) =>
                  SCORE_BUCKETS.find((b) => b.range === range)?.color ?? "#4b93ff"
                }
                xAxis={{ title: { text: "Score Range" } }}
                yAxis={{ title: { text: "Number of Quizzes" } }}
                label={{
                  position: "top",
                  style: { fill: "#374151", fontSize: 11, fontWeight: 600 },
                  formatter: (d) => (d.count > 0 ? d.count : ""),
                }}
                annotations={distStats?.avg ? [
                  {
                    type: "line",
                    start: ["min", distStats.avg > 80 ? 1 : 0],
                    end:   ["max", distStats.avg > 80 ? 1 : 0],
                    style: { stroke: "transparent" },
                  },
                ] : []}
                tooltip={{
                  formatter: (d) => {
                    const pct = totalQuizzes > 0
                      ? Math.round((d.count / totalQuizzes) * 100)
                      : 0;
                    return { name: "Quizzes", value: `${d.count} (${pct}% of total)` };
                  },
                }}
              />
            ) : (
              <div className={styles.emptyChart}>No quiz score data yet</div>
            )}
          </ChartCard>

          {/* ── Insight chips ── */}
          {distStats && (
            <div className={styles.distStatsStrip}>
              <Tooltip title="Average accuracy across all your quizzes in this view" placement="top">
                <div className={`${styles.distChip} ${styles.distChipBlue}`}>
                  <BarChartOutlined style={{ color: "#4b93ff", fontSize: 13 }} />
                  <div className={styles.distChipContent}>
                    <span className={styles.distChipLabel}>Avg Score</span>
                    <span className={styles.distChipVal}>{distStats.avg}%</span>
                  </div>
                </div>
              </Tooltip>

              <Tooltip title="Your highest single quiz score" placement="top">
                <div className={`${styles.distChip} ${styles.distChipAmber}`}>
                  <TrophyOutlined style={{ color: "#d97706", fontSize: 13 }} />
                  <div className={styles.distChipContent}>
                    <span className={styles.distChipLabel}>Personal Best</span>
                    <span className={styles.distChipVal}>{distStats.best}%</span>
                  </div>
                </div>
              </Tooltip>

              <Tooltip
                title={
                  distStats.trend === 0
                    ? "No change between your earliest and most recent quizzes"
                    : `Your recent quizzes averaged ${Math.abs(distStats.trend)}% ${distStats.trend > 0 ? "higher" : "lower"} than your earlier ones`
                }
                placement="top"
              >
                <div className={`${styles.distChip} ${distStats.trend > 0 ? styles.distChipGreen : distStats.trend < 0 ? styles.distChipRed : styles.distChipGray}`}>
                  {distStats.trend > 0
                    ? <RiseOutlined style={{ color: "#16a34a", fontSize: 13 }} />
                    : distStats.trend < 0
                      ? <FallOutlined style={{ color: "#dc2626", fontSize: 13 }} />
                      : <MinusOutlined style={{ color: "#94a3b8", fontSize: 13 }} />
                  }
                  <div className={styles.distChipContent}>
                    <span className={styles.distChipLabel}>Trend</span>
                    <span className={styles.distChipVal}>
                      {distStats.trend > 0 ? "+" : ""}{distStats.trend}%
                    </span>
                  </div>
                </div>
              </Tooltip>

              {distStats.topRange && (
                <Tooltip title={`${distStats.topRange.count} of your quizzes scored in this range`} placement="top">
                  <div className={`${styles.distChip} ${styles.distChipNeutral}`}>
                    <span
                      className={styles.distChipDot}
                      style={{ background: distStats.topColor }}
                    />
                    <div className={styles.distChipContent}>
                      <span className={styles.distChipLabel}>Top Range</span>
                      <span className={styles.distChipVal}>{distStats.topRange.range}</span>
                    </div>
                  </div>
                </Tooltip>
              )}
            </div>
          )}

          {distFetching && <div className={styles.shimmerOverlay} />}
        </div>
      </Card>

      {/* ── Performance by [Attribute] ──────────────────────────────────── */}
      <Card className={styles.analyticsCard}>
        <div className={styles.cardContentWrap}>
          <div className={styles.sectionHeader}>
            <div className={styles.headerLeft}>
              <Title level={5} className={styles.sectionTitle}>
                Performance by
              </Title>
              <Segmented
                size="small"
                value={attribute}
                onChange={setAttribute}
                options={ATTR_OPTIONS}
                className={styles.attrToggle}
              />
            </div>
            <Tooltip
              title={`Accuracy per ${ATTR_OPTIONS.find(o => o.value === attribute)?.label?.toLowerCase() ?? attribute} — all time. Sorted best → worst. Green = above 60% pass threshold.`}
              placement="topRight"
            >
              <InfoCircleOutlined className={styles.infoIcon} />
            </Tooltip>
          </div>

          {/* Passing badge row */}
          {hasAttrData && (
            <div className={styles.attrStatRow}>
              <Tooltip
                title={`${passingCount} of your ${totalCount} ${activeAttrLabel.toLowerCase()}${totalCount !== 1 ? "s are" : " is"} above the 60% pass threshold — based on all-time data`}
                placement="top"
              >
                <span className={passingCount === totalCount ? styles.badgeGreen : styles.badgeAmber}>
                  <CheckCircleOutlined />
                  {passingCount}/{totalCount} passing
                </span>
              </Tooltip>
              {attribute === "script" && (
                <span className={styles.attrNote}>
                  Avg across all attempts
                </span>
              )}
            </div>
          )}

          <ChartCard>
            {hasAttrData ? (
              <BarChart
                data={activeAttrData}
                xField="label"
                yField="accuracy"
                color={({ label }) => {
                  const d = activeAttrData.find((s) => s.label === label);
                  return d?.passing ? "#52c41a" : "#ff4d4f";
                }}
                xAxis={{ title: { text: activeAttrLabel } }}
                yAxis={{ title: { text: "Accuracy (%)" }, max: 100, min: 0 }}
                label={{
                  position: "top",
                  style: { fill: "#374151", fontSize: 11, fontWeight: 600 },
                  formatter: (d) => `${d.accuracy}%`,
                }}
                annotations={[
                  {
                    type: "line",
                    start: ["min", PASS_THRESHOLD],
                    end:   ["max", PASS_THRESHOLD],
                    style: { stroke: "#faad14", lineDash: [4, 4], lineWidth: 1.5 },
                  },
                  {
                    type: "text",
                    position: ["min", PASS_THRESHOLD],
                    content: "Pass (60%)",
                    style: { fill: "#b45309", fontSize: 10 },
                    offsetY: -6,
                    offsetX: 4,
                  },
                ]}
                tooltip={{
                  formatter: (d) => {
                    const item = activeAttrData.find((s) => s.label === d.label);
                    if (attribute === "script") {
                      return {
                        name:  item?.fullLabel ?? d.label,
                        value: `${d.accuracy}%  ·  ${item?.attempts ?? 1} attempt${item?.attempts !== 1 ? "s" : ""}`,
                      };
                    }
                    return {
                      name:  d.label,
                      value: `${d.accuracy}%  ·  ${item?.correct ?? 0} correct  /  ${item?.incorrect ?? 0} wrong`,
                    };
                  },
                }}
              />
            ) : (
              <div className={styles.emptyChart}>No {activeAttrLabel.toLowerCase()} data yet</div>
            )}
          </ChartCard>

          {/* Bottom summary chips */}
          {hasAttrData && (
            <div className={styles.specialtyStatsRow}>
              {activeAttrData.map((d) => (
                <Tooltip
                  key={d.label}
                  title={
                    attribute === "script"
                      ? `${d.fullLabel}  ·  ${d.attempts} attempt${d.attempts !== 1 ? "s" : ""}`
                      : `${d.correct} correct · ${d.incorrect} wrong · ${d.total} total`
                  }
                  placement="top"
                >
                  <div className={`${styles.specialtyChip} ${d.passing ? styles.specialtyChipPass : styles.specialtyChipFail}`}>
                    <span className={styles.specialtyChipName}>{d.label}</span>
                    <span className={styles.specialtyChipAcc}>{d.accuracy}%</span>
                  </div>
                </Tooltip>
              ))}
            </div>
          )}

          {attrFetching && <div className={styles.shimmerOverlay} />}
        </div>
      </Card>
    </div>
  );
};

export default DashboardAnalyticsRow;
